import { CRYPT_ASCUAS, INCURSION_TIERS, MAGMA_LORD, type AilmentKind } from '@somnhold/content';
import { buildTerrain, cellCenter, cellId, cellQ, cellR, heroWalkable } from '../grid.ts';
import { hexDist, hexLen, hexRing, hexToWorld, type Hex } from '../hex.ts';
import { withinRange } from '../math/fixed.ts';
import { deriveSeed, rngInt, rngRange, seedRng } from '../math/rng.ts';
import { T_GATE, T_KEEP, T_PATH, T_SPAWN, type BattleState, type DungeonRoom, type Hero } from '../types.ts';
import { damageHero, ev, markedBlow } from './combat.ts';
import { heroHome, postSpot } from './create.ts';

/*
 * Ascuas, incursion level II (docs/NIVELES.md): lava cracks that burn whoever stands on them, eruptions
 * marked on the ground a moment before they burst, and the magma lord, whose slams raise more of them.
 */

const T2 = INCURSION_TIERS[2];
/** A burst eruption lingers this long for the eye, harmless. */
const AFTERGLOW = 6;
const FOREVER = 1_000_000_000;
/** How far from a guard post (or the node) no crack opens. */
const POST_CLEAR = 1900;

/** Lays the cracks of an Ascuas battle: off the paths, gates and node, from the battle's own seed. */
export const placeCracks = (s: BattleState): void => {
  if (s.rules?.tier !== 2) return;
  if (s.map.dungeon) return placeCryptTraps(s);
  const R = s.map.radius;
  const terrain = buildTerrain(s.map);
  const rng = seedRng(deriveSeed(s.seed, 4242));
  // Never where the heroes stand guard (each path's post and the node): the lava is to be fought around, not on.
  const posts = [...s.map.sectors.map((sec) => postSpot(s, sec.index)), heroHome(s, -1)];
  const free: number[] = [];
  terrain.forEach((tr, id) => {
    if (tr === T_PATH || tr === T_GATE || tr === T_KEEP || tr === T_SPAWN) return;
    const d = hexLen(cellQ(R, id), cellR(R, id));
    if (d < 2 || d > s.map.bastion + 1 || !heroWalkable(s, id)) return;
    const c = cellCenter(R, id);
    if (posts.some((p) => withinRange(p.x, p.y, c.x, c.y, POST_CLEAR))) return;
    free.push(id);
  });
  for (let i = 0; i < T2.cracks && free.length; i++) {
    const id = free.splice(rngInt(rng, free.length), 1)[0] as number;
    const c = cellCenter(R, id);
    s.zones.push({ id: s.nextId++, kind: 'lava', x: c.x, y: c.y, radius: T2.crackRadius, cells: [id], ticks: FOREVER, every: 12, damage: T2.crackDamage, slowBp: 0, pid: '' });
  }
};

/**
 * The crypt in embers: lava cracks in every room, a fire vent in every corridor and every room (each on
 * its own beat), and the rune seal on the Warden's door in the last room before it.
 */
export const placeCryptTraps = (s: BattleState, only: (room: DungeonRoom) => boolean = () => true, withSeal = true, avoid: Hex[] = []): void => {
  const L = s.map.dungeon;
  if (!L) return;
  const R = s.map.radius;
  const rng = seedRng(deriveSeed(s.seed, 4243));
  const C = CRYPT_ASCUAS;
  const roomCells = (i: number): Hex[] => {
    const room = L.rooms[i];
    if (!room) return [];
    const out: Hex[] = [];
    for (let k = 1; k < room.radius; k++) for (const h of hexRing(k)) out.push({ q: room.q + h.q, r: room.r + h.r });
    return out;
  };
  const near = (a: Hex, list: Hex[], d: number): boolean => list.some((b) => hexDist(a, b) < d);
  let beat = 0;
  const vent = (h: Hex): void => {
    const c = hexToWorld(h.q, h.r);
    s.zones.push({ id: s.nextId++, kind: 'vent', x: c.x, y: c.y, radius: C.ventRadius, cells: [cellId(R, h.q, h.r)], ticks: FOREVER, every: C.ventEvery, damage: 0, slowBp: 0, pid: '', phase: (beat++ * 17) % C.ventEvery });
  };
  // The seal: three plates spread around the last room before the Warden's hall.
  const sealRoom = L.rooms.length - 2;
  const sr = L.rooms[sealRoom];
  const plates: Hex[] = [...avoid];
  if (sr && sealRoom > 0 && withSeal) {
    const ring = hexRing(sr.radius > 1 ? sr.radius - 1 : 1);
    const off = rngInt(rng, ring.length);
    for (let k = 0; k < C.seal.plates; k++) {
      const h = ring[(off + Math.trunc((k * ring.length) / C.seal.plates)) % ring.length] as Hex;
      plates.push({ q: sr.q + h.q, r: sr.r + h.r });
    }
    const order = [0, 1, 2];
    for (let i = order.length - 1; i > 0; i--) {
      const j = rngInt(rng, i + 1);
      [order[i], order[j]] = [order[j] as number, order[i] as number];
    }
    if (s.dungeon) s.dungeon.seal = { plates, order, step: 0, showFrom: 0, showUntil: 0, solved: false, on: [] };
  }
  L.rooms.forEach((room, i) => {
    if (room.kind !== 'room' || !only(room)) return;
    const doors = [L.doors[i - 1], L.doors[i]].filter((d): d is Hex => !!d);
    const free = roomCells(i).filter((h) => !near(h, doors, 2) && !near(h, plates, 2) && hexDist(h, room) > 0);
    for (let k = 0; k < C.cracksPerRoom && free.length; k++) {
      const h = free.splice(rngInt(rng, free.length), 1)[0] as Hex;
      const c = hexToWorld(h.q, h.r);
      s.zones.push({ id: s.nextId++, kind: 'lava', x: c.x, y: c.y, radius: T2.crackRadius, cells: [cellId(R, h.q, h.r)], ticks: FOREVER, every: 12, damage: T2.crackDamage, slowBp: 0, pid: '' });
    }
    const spot = free.filter((h) => !near(h, plates, 2));
    if (spot.length) vent(spot[rngInt(rng, spot.length)] as Hex);
  });
  // A vent in every corridor, two steps short of the door it leads to.
  const inRoom = (h: Hex): boolean => L.rooms.some((r) => hexDist(h, r) <= r.radius);
  const corridor = L.floor.filter((h) => !inRoom(h));
  for (const [i, d] of L.doors.entries()) {
    const into = L.rooms[i + 1];
    if (!into || into.kind === 'entry' || !only(into)) continue;
    const pick = corridor.filter((h) => hexDist(h, d) === 2)[0] ?? corridor.filter((h) => hexDist(h, d) === 1)[0];
    if (pick) vent(pick);
  }
};

/** Marks an eruption: it bursts where it stands once its warning runs out. */
export const placeEruption = (s: BattleState, x: number, y: number, warn: number = T2.eruptionWarn, ail?: AilmentKind): void => {
  s.zones.push({ id: s.nextId++, kind: 'eruption', x, y, radius: T2.eruptionRadius, cells: [], ticks: warn + AFTERGLOW, every: 1, damage: T2.eruptionDamage, slowBp: 0, pid: '', warn, ...(ail ? { ail } : {}) });
};

const standing = (s: BattleState): Hero[] => s.heroes.filter((h) => h.downTicks === 0);

/** Near a hero (a little off, so standing still is not the only answer). */
const nearHero = (s: BattleState, h: Hero, ail?: AilmentKind): void => placeEruption(s, h.x + rngRange(s.rng, -500, 500), h.y + rngRange(s.rng, -500, 500), T2.eruptionWarn, ail);

/** Each tick of an Ascuas battle: eruptions now and then near the heroes (more often in the last wave), and the lord's slams. */
export const ascuasTick = (s: BattleState): void => {
  // Level II, and the deep crypt's floor of embers (level III).
  if ((s.rules?.tier !== 2 && !(s.rules?.tier === 3 && s.map.dungeon)) || s.result !== 'running') return;
  const heroes = standing(s);
  if (!heroes.length) return;
  const o = s.objective;
  const last = !!o && o.progress * 3 >= o.total * 2;
  const every = last ? T2.eruptionEveryLast : T2.eruptionEvery;
  // (In the crypt the vents do this job: no eruptions out of nowhere.)
  if (!s.map.dungeon && s.tick > 0 && s.tick % every === 0 && s.enemies.some((e) => e.hp > 0)) nearHero(s, heroes[rngInt(s.rng, heroes.length)] as Hero);
  // Fire vents keep their own beat, fight or no fight.
  for (const z of s.zones) {
    if (z.kind !== 'vent' || (s.tick + (z.phase ?? 0)) % z.every !== 0) continue;
    s.zones.push({ id: s.nextId++, kind: 'eruption', x: z.x, y: z.y, radius: z.radius, cells: [], ticks: CRYPT_ASCUAS.ventWarn + AFTERGLOW, every: 1, damage: T2.eruptionDamage, slowBp: 0, pid: '', warn: CRYPT_ASCUAS.ventWarn });
  }
  for (const e of s.enemies) {
    if ((e.kind !== 'magmaLord' && (e.kind !== 'warden' || s.rules?.tier !== 2)) || e.hp <= 0) continue;
    // The lord, and in the crypt in embers the Warden, slam the ground and raise eruptions under the heroes.
    const every = e.kind === 'warden' ? CRYPT_ASCUAS.wardenSlamEvery : MAGMA_LORD.slamEvery;
    const count = e.kind === 'warden' ? CRYPT_ASCUAS.wardenSlamEruptions : MAGMA_LORD.slamEruptions;
    if (every <= 0 || (s.tick - e.born) % every !== every - 1) continue;
    ev(s, { t: 'skill', hero: e.id, skill: 'magmaSlam', x: e.x, y: e.y });
    // A slam's eruptions are its marked blow: they leave cinders on whoever they catch twice.
    for (let i = 0; i < count; i++) nearHero(s, heroes[i % heroes.length] as Hero, 'cinders');
  }
};

/** Lava and eruption zones (the rest are the heroes' own, in updateZones). */
export const ascuasZone = (s: BattleState, z: BattleState['zones'][number]): void => {
  if (z.kind === 'lava') {
    if (z.ticks % z.every !== 0) return;
    for (const h of s.heroes) if (h.downTicks === 0 && withinRange(z.x, z.y, h.x, h.y, z.radius)) damageHero(s, h, z.damage, null, 'fire');
    return;
  }
  if (z.kind !== 'eruption' || z.warn === undefined || z.warn < 0) return;
  z.warn--;
  if (z.warn > 0) return;
  for (const h of s.heroes) {
    if (h.downTicks !== 0 || !withinRange(z.x, z.y, h.x, h.y, z.radius)) continue;
    damageHero(s, h, z.damage, null, 'fire');
    if (z.ail) markedBlow(s, h, z.ail);
  }
  ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'eruption' });
  z.warn = -1;
  z.ticks = AFTERGLOW;
};
